import React, { useMemo } from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import "./AIPanel.css";

/* --- helpers --- */

function titleize(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

/** Replace {{key}} with reviews[key] */
function renderTemplate(str, data) {
  if (!str) return "";
  return str.replace(/{{\s*([^}]+)\s*}}/g, (_, path) => {
    const key = String(path).trim();
    const val = data?.[key];
    return val === undefined || val === null ? "" : String(val);
  });
}

/** Minimal evaluator for comparisons like ">4", "=high" */
function evalRange(expr, raw) {
  if (!expr) return false;
  const v = raw;
  const m = String(expr).trim().match(/^([<>]=?|=)\s*(.+)$/);
  if (!m) return false;
  const [, op, rhsRaw] = m;

  if (op === "=") return String(v).toLowerCase() === String(rhsRaw).toLowerCase();

  const rhsNum = Number(rhsRaw);
  const lhsNum = Number(v);
  if (Number.isNaN(rhsNum) || Number.isNaN(lhsNum)) return false;

  if (op === ">") return lhsNum > rhsNum;
  if (op === ">=") return lhsNum >= rhsNum;
  if (op === "<") return lhsNum < rhsNum;
  if (op === "<=") return lhsNum <= rhsNum;
  return false;
}

/** Build chips from details.{quality,confidence} */
function buildBadges(details, reviews) {
  const chips = [];

  const q = details?.quality;
  if (q?.dataSource) {
    const v = reviews?.[q.dataSource];
    if (v !== undefined) {
      const ranges = q.range || {};
      for (const [label, rule] of Object.entries(ranges)) {
        if (evalRange(rule, v)) {
          chips.push({ label: `Quality: ${label}`, key: "quality" });
          break;
        }
      }
    }
  }

  const c = details?.confidence;
  if (c?.dataSource) {
    const v = reviews?.[c.dataSource];
    if (v !== undefined && v !== "") {
      const ranges = c.range || {};
      let matched = false;
      for (const [label, rule] of Object.entries(ranges)) {
        if (evalRange(rule, v)) {
          chips.push({ label: `Confidence: ${label}`, key: "confidence" });
          matched = true;
          break;
        }
      }
      if (!matched) chips.push({ label: `Confidence: ${String(v)}`, key: "confidence" });
    }
  }

  return chips;
}

function renderValue(value) {
  if (typeof value === "boolean") {
    return <Box className="ai-field-text">{value ? "Yes" : "No"}</Box>;
  }
  if (Array.isArray(value)) {
    return (
      <Box className="ai-chip-row">
        {value.map((v, i) => (
          <Chip key={i} label={String(v)} size="small" />
        ))}
      </Box>
    );
  }
  if (typeof value === "object") {
    return (
      <Box component="pre" className="ai-pre">
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  const s = String(value);
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(s);
  if (looksLikeHtml) {
    return <Box className="ai-field-html" dangerouslySetInnerHTML={{ __html: s }} />;
  }
  return <Box className="ai-field-text">{s}</Box>;
}

/* --- component --- */

export default function AIPanel({ row, view }) {
  const reviews = row?.reviews || {};
  const fields = view?.fields || [];

  // Identify the AI field definition that sources "reviews"
  const aiFieldDef = useMemo(() => {
    return (
      fields.find((x) => x.dataSource === "reviews" && x.aiPanel === true) ||
      fields.find((x) => x.dataSource === "reviews" && x.fieldType === "aiScreening2") ||
      fields.find((x) => x.dataSource === "reviews") ||
      null
    );
  }, [fields]);

  const details = aiFieldDef?.details || {};
  const chips = useMemo(() => buildBadges(details, reviews), [details, reviews]);

  const summaryHtml = useMemo(() => {
    const t = details?.tooltip;
    if (!t) return null;
    return renderTemplate(t, reviews);
  }, [details, reviews]);

  // Filter + order strictly by attributes if provided
  const { entries } = useMemo(() => {
    const attributes = aiFieldDef?.attributes && typeof aiFieldDef.attributes === "object"
      ? aiFieldDef.attributes
      : null;

    let order = [];
    if (attributes) {
      order = Object.keys(attributes).filter((k) => attributes[k] === true);
    }

    const sourceEntries = Object.entries(reviews).filter(([, v]) => !isEmpty(v));

    const map = new Map(sourceEntries);

    const ordered = [];
    if (order.length > 0) {
      for (const k of order) {
        if (map.has(k)) {
          ordered.push([k, map.get(k)]);
          map.delete(k);
        }
      }
    }

    // append any remaining keys alphabetically (only if attributes not restricting)
    const rest =
      order.length > 0
        ? []
        : Array.from(map.entries()).sort(([a], [b]) => String(a).localeCompare(String(b)));

    const finalList = [...ordered, ...rest].map(([key, value]) => ({
      key,
      label: titleize(key),
      value,
    }));

    return { entries: finalList };
  }, [aiFieldDef, reviews]);

  return (
    <Box className="ai-panel-inner">
      {(summaryHtml || chips.length > 0) && (
        <Box className="ai-summary">
          <Box className="ai-chip-row">
            {chips.map((c, i) => (
              <Chip key={`${c.key}-${i}`} label={c.label} size="small" />
            ))}
          </Box>

          {summaryHtml && (
            <Tooltip title="AI summary" placement="top" arrow>
              <Box
                className="ai-field-html"
                dangerouslySetInnerHTML={{ __html: summaryHtml }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {entries.map((field) => (
        <Box key={`ai-${field.key}`} className="ai-section">
          <Typography variant="h6" className="ai-section-title">
            {field.label}
          </Typography>
          {renderValue(field.value)}
        </Box>
      ))}
    </Box>
  );
}
